import { compositionPageDimensionsMm, normalizedRectToMm } from '../geometry';
import { deriveScaleBarMeasurement, northArrowRotationDegrees } from '../furniture';
import type {
  CompositionDraft,
  CompositionLogoMimeType,
  CompositionTextElement,
  CompositionTypography,
  NormalizedPoint,
  NormalizedRect,
} from '../model';
import {
  COMPOSITION_LOGICAL_PIXELS_PER_MM,
  compositionLogicalViewport,
  effectiveCompositionCamera,
  hasExactCompositionScale,
  mapViewForCompositionCamera,
} from '../scale';
import { deriveCompositionLegend, type CompositionLegendLayer } from '../legend';
import {
  buildLegendLayout,
  effectiveLegendCornerRadius,
  type LegendTextMeasurement,
} from '../legendLayout';
import { containedLogoBounds } from '../logo';
import { scaleBarLayout } from '../panelAppearance';
import { scaleBarStrokeWidthMm } from '../scaleBarStroke';
import type { SourceDescriptor } from '../../data/model';
import type { LayerDefinition } from '../../project/model';
import type { AnnotationLayer } from '../../annotations/model';
import type { MapView } from '../../map/model';
import {
  STATIC_EXPORT_MAX_CANVAS_PIXELS,
  STATIC_EXPORT_MAX_PAGE_DIMENSION_PX,
  StaticExportError,
} from './model';

export interface OutputPoint {
  readonly x: number;
  readonly y: number;
}

export interface OutputRect extends OutputPoint {
  readonly width: number;
  readonly height: number;
}

export interface StaticTextPlan {
  readonly enabled: boolean;
  readonly text: string;
  readonly position: OutputPoint;
  readonly widthPx: number;
  readonly fontSizePx: number;
  readonly typography: CompositionTypography;
}

export interface StaticCompositionRenderPlan {
  readonly dpi: number;
  readonly pixelsPerMm: number;
  readonly page: {
    readonly widthMm: number;
    readonly heightMm: number;
    readonly widthPx: number;
    readonly heightPx: number;
    readonly backgroundColour: string;
  };
  readonly mapFrame: {
    readonly boundsMm: OutputRect;
    readonly boundsPx: OutputRect;
    readonly logicalViewport: {
      readonly widthPx: number;
      readonly heightPx: number;
    };
    readonly logicalPixelsPerMm: number;
    readonly backingSize: {
      readonly widthPx: number;
      readonly heightPx: number;
    };
    readonly pixelRatio: number;
    readonly borderWidthPx: number;
    readonly borderColour: string;
    readonly borderEnabled: boolean;
    readonly cornerRadiusPx: number;
    readonly view: MapView;
    readonly scaleDenominator: number;
    readonly groundWidthMetres: number;
    readonly groundHeightMetres: number;
  };
  readonly title: StaticTextPlan;
  readonly subtitle: StaticTextPlan;
  readonly sourcesAttribution: StaticTextPlan;
  readonly legend: {
    readonly columnCount: 1 | 2 | 3;
    readonly enabled: boolean;
    readonly boundsPx: OutputRect;
    readonly borderWidthPx: number;
    readonly cornerRadiusPx: number;
    readonly layout: Pick<
      CompositionDraft['legend'],
      | 'repeatContinuationHeadings'
      | 'symbolSizePercent'
      | 'paddingTopMm'
      | 'paddingRightMm'
      | 'paddingBottomMm'
      | 'paddingLeftMm'
      | 'groupSpacingMm'
      | 'itemSpacingMm'
    >;
    readonly layers: readonly CompositionLegendLayer[];
    readonly background: CompositionDraft['legend']['background'];
    readonly border: CompositionDraft['legend']['border'];
    readonly bodyTypography: CompositionTypography;
    readonly heading: CompositionDraft['legend']['heading'];
  };
  readonly scaleBar: null | {
    readonly style: 'line' | 'segmented' | 'simple-segmented';
    readonly labelPosition: 'above' | 'below';
    readonly midpointLabel: string;
    readonly panel: CompositionDraft['scaleBar']['panel'];
    readonly layout: ReturnType<typeof scaleBarLayout>;
    readonly position: OutputPoint;
    readonly widthPx: number;
    readonly physicalWidthMm: number;
    readonly groundDistanceMetres: number;
    readonly label: string;
    readonly colour: string;
    readonly fontSizePt: number;
    readonly fontSizePx: number;
    readonly thicknessMm: number;
    readonly thicknessPx: number;
  };
  readonly northArrow: null | {
    readonly position: OutputPoint;
    readonly sizePx: number;
    readonly sizeMm: number;
    readonly colour: string;
    readonly showLabel: boolean;
    readonly rotationDegrees: number;
    readonly style: CompositionDraft['northArrow']['style'];
  };
  readonly insetMap: null | {
    readonly config: NonNullable<CompositionDraft['insetMap']>;
    readonly boundsMm: OutputRect;
    readonly boundsPx: OutputRect;
  };
  readonly logo: null | {
    readonly boundsPx: OutputRect;
    readonly dataUrl: string;
    readonly mimeType: CompositionLogoMimeType;
  };
}

export const pixelsPerMmForDpi = (dpi: number): number => dpi / 25.4;
export const pointsToPixels = (points: number, dpi: number): number => (points * dpi) / 72;

function rectToPixels(rect: NormalizedRect, width: number, height: number): OutputRect {
  const left = Math.round(rect.x * width);
  const top = Math.round(rect.y * height);
  const right = Math.round((rect.x + rect.width) * width);
  const bottom = Math.round((rect.y + rect.height) * height);
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function pointToPixels(point: NormalizedPoint, width: number, height: number): OutputPoint {
  return { x: point.x * width, y: point.y * height };
}

function textPlan(
  element: CompositionTextElement,
  pageWidthPx: number,
  pageHeightPx: number,
  dpi: number,
): StaticTextPlan {
  return {
    enabled: element.enabled,
    text: element.text,
    position: pointToPixels(element.position, pageWidthPx, pageHeightPx),
    widthPx: Math.max(0.02, 0.94 - element.position.x) * pageWidthPx,
    fontSizePx: pointsToPixels(element.typography.fontSizePt, dpi),
    typography: element.typography,
  };
}

export function assertSafeStaticCanvas(width: number, height: number, dpi?: number): void {
  if (
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width <= 0 ||
    height <= 0 ||
    width > STATIC_EXPORT_MAX_PAGE_DIMENSION_PX ||
    height > STATIC_EXPORT_MAX_PAGE_DIMENSION_PX ||
    width * height > STATIC_EXPORT_MAX_CANVAS_PIXELS
  ) {
    throw new StaticExportError(
      'page-limit',
      dpi
        ? `${dpi} DPI is too large for this page size. Choose a lower resolution.`
        : 'This page is too large to export at the selected resolution.',
    );
  }
}

export function buildStaticCompositionPlan(
  composition: CompositionDraft,
  sources: readonly SourceDescriptor[],
  layers: readonly LayerDefinition[],
  dpi: number,
  output: 'raster' | 'vector' = 'raster',
  attributionText?: string,
  mapLogicalPixelsPerMm = COMPOSITION_LOGICAL_PIXELS_PER_MM,
  annotationLayers: readonly AnnotationLayer[] = [],
): StaticCompositionRenderPlan {
  const pixelsPerMm = pixelsPerMmForDpi(dpi);
  const pageMm = compositionPageDimensionsMm(composition.page);
  const pageWidthPx =
    output === 'vector' ? pageMm.width * pixelsPerMm : Math.round(pageMm.width * pixelsPerMm);
  const pageHeightPx =
    output === 'vector' ? pageMm.height * pixelsPerMm : Math.round(pageMm.height * pixelsPerMm);
  if (output === 'raster') assertSafeStaticCanvas(pageWidthPx, pageHeightPx, dpi);
  const outputRect = (rect: NormalizedRect): OutputRect =>
    output === 'raster'
      ? rectToPixels(rect, pageWidthPx, pageHeightPx)
      : {
          x: rect.x * pageWidthPx,
          y: rect.y * pageHeightPx,
          width: rect.width * pageWidthPx,
          height: rect.height * pageHeightPx,
        };
  const boundsPx = outputRect(composition.mapFrame.bounds);
  const boundsMm = normalizedRectToMm(composition.mapFrame.bounds, pageMm);
  const { widthPx: logicalWidthPx, heightPx: logicalHeightPx } = compositionLogicalViewport(
    boundsMm.width,
    boundsMm.height,
    mapLogicalPixelsPerMm,
  );
  const pixelRatio = pixelsPerMm / mapLogicalPixelsPerMm;
  const backingWidthPx = Math.floor(logicalWidthPx * pixelRatio);
  const backingHeightPx = Math.floor(logicalHeightPx * pixelRatio);
  const effectiveCamera = effectiveCompositionCamera(
    composition.mapFrame.camera,
    boundsMm.width,
    boundsMm.height,
    mapLogicalPixelsPerMm,
  );
  const view = mapViewForCompositionCamera(effectiveCamera, mapLogicalPixelsPerMm);
  if (!view) {
    throw new StaticExportError('output-limit', 'The authored map scale cannot be exported.');
  }
  const measurement =
    composition.scaleBar.enabled && hasExactCompositionScale(composition.mapFrame.camera.pitch)
      ? deriveScaleBarMeasurement(
          effectiveCamera.scaleDenominator,
          composition.scaleBar.maximumWidthMm,
          composition.scaleBar.units,
        )
      : null;

  return {
    dpi,
    pixelsPerMm,
    page: {
      widthMm: pageMm.width,
      heightMm: pageMm.height,
      widthPx: pageWidthPx,
      heightPx: pageHeightPx,
      backgroundColour: composition.page.backgroundColour,
    },
    mapFrame: {
      boundsMm,
      boundsPx,
      logicalViewport: { widthPx: logicalWidthPx, heightPx: logicalHeightPx },
      logicalPixelsPerMm: mapLogicalPixelsPerMm,
      backingSize: { widthPx: backingWidthPx, heightPx: backingHeightPx },
      pixelRatio,
      borderWidthPx: composition.mapFrame.border.widthMm * pixelsPerMm,
      borderColour: composition.mapFrame.border.colour,
      borderEnabled: composition.mapFrame.border.enabled,
      cornerRadiusPx: effectiveLegendCornerRadius(
        (composition.mapFrame.cornerRadiusMm ?? 0) * pixelsPerMm,
        boundsPx.width,
        boundsPx.height,
      ),
      view,
      scaleDenominator: effectiveCamera.scaleDenominator,
      groundWidthMetres: (boundsMm.width * effectiveCamera.scaleDenominator) / 1000,
      groundHeightMetres: (boundsMm.height * effectiveCamera.scaleDenominator) / 1000,
    },
    insetMap:
      composition.insetMap && composition.insetMap.enabled !== false
        ? {
            config: composition.insetMap,
            boundsMm: normalizedRectToMm(composition.insetMap.bounds, pageMm),
            boundsPx: outputRect(composition.insetMap.bounds),
          }
        : null,
    title: textPlan(composition.title, pageWidthPx, pageHeightPx, dpi),
    subtitle: textPlan(composition.subtitle, pageWidthPx, pageHeightPx, dpi),
    sourcesAttribution: {
      enabled: attributionText !== undefined,
      text: attributionText ?? '',
      position: pointToPixels(composition.sourcesAttribution.position, pageWidthPx, pageHeightPx),
      widthPx:
        Math.min(
          composition.sourcesAttribution.width ??
            Math.max(0, 1 - composition.sourcesAttribution.position.x),
          Math.max(0, 1 - composition.sourcesAttribution.position.x),
        ) * pageWidthPx,
      fontSizePx: pointsToPixels(composition.sourcesAttribution.typography.fontSizePt, dpi),
      typography: composition.sourcesAttribution.typography,
    },
    legend: {
      columnCount: composition.legend.columnCount ?? 1,
      enabled: composition.legend.enabled,
      // Furniture retains subpixel physical bounds, so DPI cannot change text/column breaks.
      boundsPx: {
        x: composition.legend.bounds.x * pageMm.width * pixelsPerMm,
        y: composition.legend.bounds.y * pageMm.height * pixelsPerMm,
        width: composition.legend.bounds.width * pageMm.width * pixelsPerMm,
        height: composition.legend.bounds.height * pageMm.height * pixelsPerMm,
      },
      borderWidthPx: composition.legend.border.widthMm * pixelsPerMm,
      cornerRadiusPx: effectiveLegendCornerRadius(
        composition.legend.cornerRadiusMm * pixelsPerMm,
        outputRect(composition.legend.bounds).width,
        outputRect(composition.legend.bounds).height,
      ),
      layout: {
        paddingTopMm: composition.legend.paddingTopMm,
        paddingRightMm: composition.legend.paddingRightMm,
        paddingBottomMm: composition.legend.paddingBottomMm,
        paddingLeftMm: composition.legend.paddingLeftMm,
        repeatContinuationHeadings: composition.legend.repeatContinuationHeadings ?? true,
        symbolSizePercent: composition.legend.symbolSizePercent ?? 100,
        groupSpacingMm: composition.legend.groupSpacingMm,
        itemSpacingMm: composition.legend.itemSpacingMm,
      },
      layers: deriveCompositionLegend(
        sources,
        layers,
        composition.legend.excludedLayerIds,
        composition.legend.excludedEntryKeysByLayer,
        annotationLayers,
        composition.legend.hiddenGroupHeadingIds,
      ),
      background: composition.legend.background,
      border: composition.legend.border,
      bodyTypography: composition.legend.bodyTypography,
      heading: composition.legend.heading,
    },
    scaleBar: measurement
      ? {
          style: composition.scaleBar.style ?? 'line',
          labelPosition: composition.scaleBar.labelPosition ?? 'above',
          midpointLabel: measurement.midpointLabel,
          panel: composition.scaleBar.panel,
          layout: scaleBarLayout(
            measurement.physicalWidthMm * pixelsPerMm,
            pointsToPixels(composition.scaleBar.fontSizePt, dpi),
            composition.scaleBar.thicknessMm * pixelsPerMm,
            composition.scaleBar.panel,
            pixelsPerMm,
            composition.scaleBar.style === 'segmented' ||
              composition.scaleBar.style === 'simple-segmented',
            composition.scaleBar.style === 'simple-segmented' &&
              composition.scaleBar.labelPosition !== 'below',
          ),
          position: pointToPixels(composition.scaleBar.position, pageWidthPx, pageHeightPx),
          widthPx: measurement.physicalWidthMm * pixelsPerMm,
          physicalWidthMm: measurement.physicalWidthMm,
          groundDistanceMetres: measurement.groundDistanceMetres,
          label: measurement.label,
          colour: composition.scaleBar.colour,
          fontSizePt: composition.scaleBar.fontSizePt,
          fontSizePx: pointsToPixels(composition.scaleBar.fontSizePt, dpi),
          thicknessMm: composition.scaleBar.thicknessMm,
          thicknessPx:
            scaleBarStrokeWidthMm(composition.scaleBar.thicknessMm, mapLogicalPixelsPerMm) *
            pixelsPerMm,
        }
      : null,
    northArrow: composition.northArrow.enabled
      ? {
          position: pointToPixels(composition.northArrow.position, pageWidthPx, pageHeightPx),
          sizePx: composition.northArrow.sizeMm * pixelsPerMm,
          sizeMm: composition.northArrow.sizeMm,
          colour: composition.northArrow.colour,
          showLabel: composition.northArrow.showLabel,
          rotationDegrees: northArrowRotationDegrees(composition.mapFrame.camera.bearing),
          style: composition.northArrow.style,
        }
      : null,
    logo: composition.logo
      ? {
          boundsPx: containedLogoBounds(
            outputRect(composition.logo.bounds),
            composition.logo.aspectRatio,
          ),
          dataUrl: composition.logo.dataUrl,
          mimeType: composition.logo.mimeType,
        }
      : null,
  };
}

/** Build the same millimetre plan from any output density; no output-specific wrapping. */
export function staticLegendLayout(
  plan: StaticCompositionRenderPlan,
  measure: LegendTextMeasurement,
) {
  const legend = plan.legend;
  const mm = (value: number) => Math.round((value / plan.pixelsPerMm) * 1e9) / 1e9;
  return buildLegendLayout(
    {
      ...legend.layout,
      columnCount: legend.columnCount,
      bodyTypography: legend.bodyTypography,
      heading: legend.heading,
      border: { ...legend.border, widthMm: mm(legend.borderWidthPx) },
    },
    legend.layers,
    mm(legend.boundsPx.width),
    mm(legend.boundsPx.height),
    measure,
  );
}
